import { useEffect, useRef, useState } from "react";
import { WindowContent, WindowTitleBar } from "../../desktopWindow/components/DesktopWindow";
import { FileData } from "../../context/GameContext";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import { useWindowTilesContext } from "desktopWindow/context/WindowTilesContext";
import WaveSurfer from "wavesurfer.js";
import { useTheme } from "@mui/styles";
import { lighten } from "@mui/system";
import { Box, IconButton, Slider, Stack, Typography } from "@mui/material";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowBack, FastForward, FastRewind, PauseCircleFilled, VolumeDown } from "@mui/icons-material";
import { isDesktop } from "react-device-detect";
import { ReactComponent as MusicIcon } from "assets/svg/game-audio-music.svg";
import { ActionBar } from "desktopWindow/components/DesktopWindow.styled";
import { getInvertColor } from "utils/themeUtils";

interface Props {
	file: FileData;
	width: number;
	height: number;
	handleFileForMobile?: () => void;
	name: string | undefined;
	openFromApp?: boolean;
}
export const AudioViewer = ({ file, handleFileForMobile, name, openFromApp }: Props) => {
	const { MobileWindowResized } = useWindowTilesContext();

	useEffect(() => {
		window.setTimeout(() => {
			MobileWindowResized("FileViewer", false);
		}, 50);
	}, [MobileWindowResized]);
	const filename = file.name.split(".")[0];
	return (
		<>
			{(isDesktop || name) && (
				<WindowTitleBar
					icon={
						<AudiotrackIcon
							height="30"
							width="30"
							sx={(theme) => ({ fill: getInvertColor(theme.palette.primary.main) })}
						/>
					}
				/>
			)}
			{!isDesktop && openFromApp && (
				<ActionBar sx={{ paddingLeft: 0, height: "68px", justifyContent: "flex-start !important" }}>
					<IconButton color="inherit" onClick={handleFileForMobile} size="large">
						<ArrowBack style={{ color: "white" }} />
					</IconButton>
				</ActionBar>
			)}
			<WindowContent
				style={{
					backgroundColor: "#fff",
					display: "flex",
					justifyContent: "center",
					alignContent: "center",
					height: "100px ",
					position: "relative",
				}}
			>
				{isDesktop ? (
					<DesktopAudioViewer file={file} name={filename} />
				) : (
					<MobileAudioViewer file={file} name={filename} />
				)}
			</WindowContent>
		</>
	);
};

const AudioDuration = ({ waveSurfer, isPlaying }: { waveSurfer: WaveSurfer; isPlaying: boolean }) => {
	const [, rerender] = useState(0);

	useEffect(() => {
		const interval = setInterval(() => {
			rerender((prev) => prev + 1);
		}, 1000);
		return () => clearInterval(interval);
	}, [isPlaying]);

	if (!waveSurfer) {
		return null;
	}

	// Generate time in minutes and seconds
	const minutes = Math.floor(waveSurfer.getCurrentTime() / 60);
	const seconds = Math.floor(waveSurfer.getCurrentTime() - minutes * 60);
	const durationMinutes = Math.floor(waveSurfer.getDuration() / 60);
	const durationSeconds = Math.floor(waveSurfer.getDuration() - durationMinutes * 60);

	return (
		<Box sx={{ display: "flex", justifyContent: "space-between" }}>
			<Box sx={{ backgroundColor: "#222222", padding: "4px 8px", borderRadius: "4px" }}>
				<Typography variant="caption" sx={{ fontSize: "0.875rem", color: "#fff", fontWeight: 600 }}>
					{`${minutes}:${seconds < 10 ? "0" + seconds : seconds}`}
				</Typography>
			</Box>
			<Box sx={{ backgroundColor: "#222222", padding: "4px 8px", borderRadius: "4px" }}>
				<Typography variant="caption" sx={{ fontSize: "0.875rem", color: "#fff", fontWeight: 600 }}>
					{`${durationMinutes}:${durationSeconds < 10 ? "0" + durationSeconds : durationSeconds}`}
				</Typography>
			</Box>
		</Box>
	);
};
const DesktopAudioViewer = ({ file, name }: Pick<Props, "file" | "name">) => {
	const theme = useTheme();
	const waveformRef = useRef(null);
	const [waveSurfer, setWaveSurfer] = useState<WaveSurfer | null>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [isVolumeControlVisible, setVolumeControlVisible] = useState(false);
	const [volume, setVolume] = useState(1); // Initial volume (1 is 100%)

	useEffect(() => {
		if (waveformRef.current && !waveSurfer && file.fileUrl) {
			// Create the WaveSurfer instance
			const _wavesurfer = WaveSurfer.create({
				container: waveformRef.current,
				waveColor: lighten(theme.palette.primary.main, 0.5),
				progressColor: theme.palette.primary.main,
				cursorColor: "#333",
				height: 50,
				interact: true, // Allows the waveform to be clicked or dragged to seek
				barWidth: 3, // Set the bar width to make the line wider
				barHeight: 1, // Adjusts the bar height (0 to 1, relative to the wave height)
				barGap: 2, // Sets the gap between the bars
				barRadius: 8, // Rounds the bar edges
			});

			_wavesurfer.load(file.fileUrl!);
			setWaveSurfer(_wavesurfer);

			// Cleanup on component unmount
			// return () => _wavesurfer.destroy();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [file.fileUrl, waveSurfer]);

	const filename = name?.split(".")[0];

	const handleVolumeChange = (event, newVolume) => {
		setVolume(newVolume);
		if (waveSurfer) {
			waveSurfer.setVolume(newVolume); // Set volume in WaveSurfer
		}
	};
	const handlePlayPause = () => {
		if (waveSurfer) {
			waveSurfer.playPause();
			setIsPlaying(!isPlaying);
		}
	};

	const toggleVolumeControl = () => {
		setVolumeControlVisible(!isVolumeControlVisible);
	};

	return (
		<Stack spacing={2} direction="column" width="100%" padding="18px">
			<Stack direction="row" spacing={1} justifyContent="space-between" pr={1}>
				<Stack direction="row" spacing={1} alignItems={"center"}>
					<IconButton color="primary" sx={{ width: 34, height: 34 }} onClick={handlePlayPause}>
						{isPlaying ? (
							<PauseCircleFilled sx={{ width: 34, height: 34 }} />
						) : (
							<PlayCircleFilledIcon sx={{ width: 34, height: 34 }} />
						)}
					</IconButton>
					<Typography sx={{ fontSize: "1.125rem" }}>{filename}</Typography>
				</Stack>
				<Stack
					direction="row"
					spacing={1}
					alignItems={"center"}
					marginRight={isVolumeControlVisible ? "8px" : 0}
				>
					<IconButton sx={{ width: 14, height: 14 }} onClick={toggleVolumeControl}>
						<VolumeUpIcon style={{ width: 14, height: 14 }} />
					</IconButton>
					<AnimatePresence>
						{isVolumeControlVisible && (
							<motion.div
								initial={{ opacity: 0, x: -20 }}
								animate={{ opacity: 1, x: 0 }}
								exit={{ opacity: 0, x: -20 }}
								transition={{ duration: 0.2 }}
							>
								<Slider
									value={volume}
									onChange={handleVolumeChange}
									min={0}
									max={1}
									step={0.01}
									style={{ width: 80, padding: "4px 0" }}
									size="small"
								/>
							</motion.div>
						)}
					</AnimatePresence>
				</Stack>
			</Stack>
			<div style={{ width: "100%" }}>
				<div ref={waveformRef} />{" "}
			</div>
			<AudioDuration waveSurfer={waveSurfer!} isPlaying={isPlaying} />
		</Stack>
	);
};

const MobileAudioViewer = ({ file, name }: Pick<Props, "file" | "name">) => {
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [volume, setVolume] = useState(1);

	useEffect(() => {
		const audio = audioRef.current;
		if (audio) {
			audio.volume = volume; // Set initial volume
			const updateTime = () => setCurrentTime(audio.currentTime);
			const setAudioData = () => setDuration(audio.duration);
			const endAudio = () => setIsPlaying(false);

			audio.addEventListener("timeupdate", updateTime);
			audio.addEventListener("loadedmetadata", setAudioData);
			audio.addEventListener("ended", endAudio);

			return () => {
				audio.removeEventListener("timeupdate", updateTime);
				audio.removeEventListener("ended", endAudio);
				audio.removeEventListener("loadedmetadata", setAudioData);
			};
		}
	}, [volume]);
	const togglePlayPause = () => {
		const audio = audioRef.current;
		if (audio) {
			if (isPlaying) {
				audio.pause();
			} else {
				audio.play();
			}
			setIsPlaying(!isPlaying);
		}
	};

	const handleSliderChange = (event, newValue) => {
		const audio = audioRef.current;
		if (audio) {
			audio.currentTime = newValue;
			setCurrentTime(newValue);
		}
	};
	const handleVolumeChange = (event, newVolume) => {
		setVolume(newVolume);
		const audio = audioRef.current;
		if (audio) {
			audio.volume = newVolume;
		}
	};

	const minutes = Math.floor(currentTime / 60);
	const seconds = Math.floor(currentTime - minutes * 60);
	const durationMinutes = Math.floor(duration / 60);
	const durationSeconds = Math.floor(duration - durationMinutes * 60);

	return (
		<Stack alignItems="center" width="100%" padding="48px" position="fixed" bottom="100px" left="0">
			<Box
				borderRadius="6px"
				height="220px"
				width="220px"
				display="flex"
				alignItems="center"
				justifyContent="center"
				marginBottom="48px"
				sx={{ backgroundColor: "#D9D9D9" }}
			>
				<MusicIcon />
			</Box>
			<Typography sx={{ fontSize: "1.125rem", fontWeight: "bold", marginBottom: "24px" }}>{name}</Typography>
			{/* Duration Slider */}
			<Box sx={{ display: "flex", justifyContent: "space-between", width: "90%" }}>
				<Typography variant="caption" sx={{ fontSize: "0.875rem", color: "#828282", fontWeight: 500 }}>
					{`${minutes}:${seconds < 10 ? "0" + seconds : seconds}`}
				</Typography>
				<Typography variant="caption" sx={{ fontSize: "0.875rem", color: "#828282", fontWeight: 500 }}>
					{`${durationMinutes}:${durationSeconds < 10 ? "0" + durationSeconds : durationSeconds}`}
				</Typography>
			</Box>
			<Slider
				value={currentTime}
				onChange={handleSliderChange}
				min={0}
				max={duration}
				step={0.1}
				sx={{ mt: -2, p: 0 }}
			/>
			<Stack
				direction="row"
				spacing={1}
				alignItems={"center"}
				justifyContent="center"
				width={"100%"}
				margin="40px 0"
			>
				<IconButton color="primary" sx={{ width: 48, height: 48 }}>
					<FastRewind />
				</IconButton>
				<IconButton color="primary" sx={{ width: 48, height: 48 }} onClick={togglePlayPause}>
					{isPlaying ? (
						<PauseCircleFilled sx={{ width: 48, height: 48 }} />
					) : (
						<PlayCircleFilledIcon sx={{ width: 48, height: 48 }} />
					)}
				</IconButton>
				<IconButton color="primary" sx={{ width: 48, height: 48 }}>
					<FastForward />
				</IconButton>
			</Stack>
			<Stack direction="row" spacing={1} alignItems={"center"} width={"100%"}>
				<IconButton>
					<VolumeDown />
				</IconButton>
				<Slider
					value={volume}
					onChange={handleVolumeChange}
					min={0}
					max={1}
					step={0.01}
					style={{ padding: "4px 0", margin: "0 8px" }}
					size="small"
				/>
				<IconButton>
					<VolumeUpIcon />
				</IconButton>
			</Stack>{" "}
			{/* Audio Element */}
			<audio ref={audioRef} src={file.fileUrl!} />
		</Stack>
	);
};
